import type { RowData } from "@tanstack/react-table";

import type { ExtendedColumnDef } from "./columnTypes";

/** Objects/arrays are skipped rather than stringified ("[object Object]"
 *  must never be searchable text). */
function primitiveText(value: unknown): string | null {
  switch (typeof value) {
    case "string":
      return value;
    case "number":
    case "boolean":
    case "bigint":
      return String(value);
    default:
      return null;
  }
}

/**
 * One row's searchable text: lowercased plain-text built from the visible
 * columns' `textValue` (display formatting) or raw accessor value.
 */
export function rowSearchText<TRow extends RowData>(
  row: TRow,
  columns: ExtendedColumnDef<TRow>[]
): string {
  const parts: string[] = [];
  for (const column of columns) {
    let text: string | null = null;
    if (column.textValue) {
      text = column.textValue(row);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if ("accessorFn" in column && column.accessorFn) {
      text = primitiveText(column.accessorFn(row, 0));
    }
    if (text) parts.push(text);
  }
  // Newline separator: never matches a typed term, so a term can't match
  // across the boundary between adjacent columns' text.
  return parts.join("\n").toLowerCase();
}

/**
 * Search index for the grid find band: searchable text per row id.
 * Data-level — searches all rows, not just the virtualized window.
 * Insertion order follows `rows`, so match order is row order.
 */
export function buildSearchIndex<TRow extends RowData>(
  rows: TRow[],
  columns: ExtendedColumnDef<TRow>[],
  getRowId: (row: TRow) => string
): Map<string, string> {
  const index = new Map<string, string>();
  for (const row of rows) {
    index.set(getRowId(row), rowSearchText(row, columns));
  }
  return index;
}

/** Row ids whose search text contains `term` (case-insensitive), in row
 *  order. An empty term matches nothing. */
export function findMatches(
  index: Map<string, string>,
  term: string
): string[] {
  if (!term) return [];
  const lowerTerm = term.toLowerCase();
  const ids: string[] = [];
  for (const [id, text] of index) {
    if (text.includes(lowerTerm)) ids.push(id);
  }
  return ids;
}
